import { supabase } from '../supabase-config';
import { cacheUserProfile } from '../services/authService';

// Robust authentication handler with multiple fallback strategies
export class AuthHandler {
  constructor() {
    this.verificationAttempts = 0;
    this.maxAttempts = 3;
    this.authStateListeners = new Set();
  }

  // Normalize phone number
  normalizePhone(phone, withPlus = true) {
    let cleaned = phone.replace(/\D/g, '');
    if (!cleaned.startsWith('1') && cleaned.length === 10) {
      cleaned = '1' + cleaned;
    }
    return withPlus ? '+' + cleaned : cleaned;
  }

  // Strategy 1: Standard OTP verification
  async strategyStandardOtp(phone, token) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Strategy 1: Standard OTP verification');
    }

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: this.normalizePhone(phone),
        token: token,
        type: 'sms'
      });

      if (!error && data?.session) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Strategy 1 successful');
        }
        return { success: true, data, method: 'standard' };
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Strategy 1 failed:', error?.message);
      }
      return { success: false, error };
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Strategy 1 exception:', err.message);
      }
      return { success: false, error: err };
    }
  }

  // Strategy 2: Auth State Change Detection (MOST RELIABLE)
  async strategyAuthStateChange(phone, token) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Strategy 2: Auth state change detection (PRIMARY)');
    }

    return new Promise((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          if (process.env.NODE_ENV === 'development') {
            console.log('Strategy 2 timeout after 10s - trying next strategy');
          }
          resolve({ success: false, error: 'Auth state timeout' });
        }
      }, 10000); // 10 second timeout

      // Set up listener before verification
      const listener = supabase.auth.onAuthStateChange((event, session) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth event in strategy 2:', event);
        }

        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') && session && !resolved) {
          resolved = true;
          clearTimeout(timeout);
          listener.data.subscription.unsubscribe();
          if (process.env.NODE_ENV === 'development') {
            console.log('Strategy 2 successful via auth state');
          }
          resolve({
            success: true,
            session,
            method: 'authStateChange'
          });
        }
      });

      // Fire verification without waiting
      supabase.auth.verifyOtp({
        phone: this.normalizePhone(phone),
        token: token,
        type: 'sms'
      }).then(({ data, error }) => {
        if (!resolved) {
          if (data?.session) {
            resolved = true;
            clearTimeout(timeout);
            listener.data.subscription.unsubscribe();
            if (process.env.NODE_ENV === 'development') {
              console.log('Strategy 2 successful via promise');
            }
            resolve({ success: true, data, method: 'promise' });
          } else if (error) {
            // Don't resolve on error - wait for auth state
            if (process.env.NODE_ENV === 'development') {
              console.log('Verify promise failed, waiting for auth state:', error.message);
            }
          }
        }
      }).catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Verify promise exception:', err.message);
        }
        // Don't resolve - wait for auth state
      });
    });
  }

  // Strategy 3: Manual session check
  async strategyManualSessionCheck(phone, token) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Strategy 3: Manual session check');
    }

    // First attempt verification
    await supabase.auth.verifyOtp({
      phone: this.normalizePhone(phone),
      token: token,
      type: 'sms'
    }).catch(() => {}); // Ignore errors

    // Wait a bit for session to establish
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Check if session exists
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (session) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Strategy 3 successful - session found');
        }
        return { success: true, session, method: 'manualCheck' };
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Strategy 3 failed - no session');
      }
      return { success: false, error: error || 'No session found' };
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Strategy 3 exception:', err.message);
      }
      return { success: false, error: err };
    }
  }

  // Strategy 4: Force refresh and retry
  async strategyForceRefresh(phone, token) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Strategy 4: Force refresh and retry');
    }

    try {
      // Clear any existing session
      await supabase.auth.signOut();

      // Wait a moment
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Try verification again
      const { data, error } = await supabase.auth.verifyOtp({
        phone: this.normalizePhone(phone),
        token: token,
        type: 'sms'
      });

      if (!error && data?.session) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Strategy 4 successful');
        }
        return { success: true, data, method: 'forceRefresh' };
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Strategy 4 failed:', error?.message);
      }
      return { success: false, error };
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Strategy 4 exception:', err.message);
      }
      return { success: false, error: err };
    }
  }

  // Main verification orchestrator
  async verifyOtp(phone, token) {
    // Security: Never log sensitive credentials
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Starting OTP verification');
    }

    this.verificationAttempts++;
    
    // Try strategies in order (Auth state change is most reliable)
    const strategies = [
      () => this.strategyAuthStateChange(phone, token), // Most reliable - works around Supabase promise issues
      () => this.strategyStandardOtp(phone, token),      // Standard approach - sometimes doesn't resolve
      () => this.strategyManualSessionCheck(phone, token), // Fallback - check session manually
      () => this.strategyForceRefresh(phone, token)      // Last resort - clear and retry
    ];

    for (let i = 0; i < strategies.length; i++) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Attempting strategy ${i + 1} of ${strategies.length}`);
      }

      try {
        const result = await strategies[i]();

        if (result.success) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Verification successful using strategy ${i + 1}`);
          }

          // Sync user profile BEFORE returning - this caches the profile
          // so AuthContext.resolveProfile will get a cache hit
          const session = result.session || result.data?.session;
          const profile = await this.ensureUserProfile(session);

          return {
            success: true,
            method: result.method,
            strategyIndex: i + 1,
            session: session,
            profile: profile  // Include profile for immediate use
          };
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`Strategy ${i + 1} failed:`, err.message);
        }
      }
    }

    // All strategies failed
    if (process.env.NODE_ENV === 'development') {
      console.error('All verification strategies failed');
    }
    return {
      success: false,
      error: 'Verification failed. Please try again or contact support.',
      attempts: this.verificationAttempts
    };
  }

  // Ensure user profile exists using SECURITY DEFINER RPC
  // This bypasses RLS to handle legacy records where auth.uid() may not match users.id
  async ensureUserProfile(session) {
    if (!session?.user) return null;

    console.log('[authHandler] Syncing user profile via RPC...');

    const userId = session.user.id;
    const phone = session.user.phone;
    const email = session.user.email;

    try {
      // Call the privileged RPC function that bypasses RLS
      const { data, error } = await supabase.rpc('sync_user_profile_on_login', {
        p_auth_id: userId,
        p_phone: phone,
        p_email: email
      });

      if (error) {
        console.error('[authHandler] RPC error:', error.message);
        return null;
      }

      if (!data?.success) {
        console.error('[authHandler] RPC failed:', data?.error);
        return null;
      }

      const profile = data.profile;
      console.log(`[authHandler] Profile synced (${data.action}):`, profile.full_name, profile.role);

      // Cache the profile immediately for resolveProfile to use
      cacheUserProfile(profile);

      return profile;
    } catch (err) {
      console.error('[authHandler] Profile sync error:', err.message);
      return null;
    }
  }

  // Send OTP with retry logic and exponential backoff
  async sendOtp(phone) {
    // Security: Don't log phone numbers
    if (process.env.NODE_ENV === 'development') {
      console.log('📱 Sending OTP...');
    }
    
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      attempts++;
      
      try {
        const { data, error } = await supabase.auth.signInWithOtp({
          phone: this.normalizePhone(phone),
          options: {
            channel: 'sms',
            shouldCreateUser: true
          }
        });

        if (!error) {
          console.log('✅ OTP sent successfully');
          return { success: true, data };
        }

        // Check error type
        if (error.message.includes('rate')) {
          console.error('⚠️ Rate limited');
          return { 
            success: false, 
            error: 'Too many attempts. Please wait a few minutes and try again.',
            rateLimited: true
          };
        }

        if (attempts < maxAttempts) {
          // Exponential backoff: 1s, 2s, 4s (prevents brute force)
          const backoffMs = 1000 * Math.pow(2, attempts - 1);
          if (process.env.NODE_ENV === 'development') {
            console.log(`⚠️ Attempt ${attempts} failed, retrying in ${backoffMs}ms...`);
          }
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }
      } catch (err) {
        // Security: Don't expose internal error details
        if (process.env.NODE_ENV === 'development') {
          console.error(`❌ Send attempt ${attempts} failed:`, err.message);
        }
      }
    }

    return {
      success: false,
      error: 'Failed to send verification code. Please check your phone number and try again.'
    };
  }
}

// Singleton instance
export const authHandler = new AuthHandler();

// Export for console access (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.authHandler = authHandler;
  console.log('Auth handler available via window.authHandler');
}